import React from 'react';
import { Link } from 'react-router-dom';
import './LegalPages.css';

const AccountDeletionPage = () => {
  return (
    <div className="legal-page">
      <div className="legal-container">
        <div className="legal-header">
          <h1>How to Delete Your Account</h1>
          <p className="last-updated">Last updated: June 17th, 2025</p>
        </div>

        <div className="legal-content">
          <section className="legal-section">
            <p>
              We respect your right to control your personal data. If you decide to delete your Fagi Errands account,
              this page explains exactly how to do it, what happens to your data, and what to consider before you proceed.
            </p>
          </section>

          <section className="legal-section">
            <h2>Before You Delete</h2>
            <p>Please consider the following before deleting your account:</p>
            <ul>
              <li><strong>Active orders:</strong> You cannot delete your account while you have active or pending orders. Please wait for all orders to complete or cancel them first.</li>
              <li><strong>Outstanding balance:</strong> Any outstanding payments or refunds must be settled before deletion.</li>
              <li><strong>Data loss:</strong> Deleting your account is permanent. Your order history, saved addresses, payment methods, and referral rewards will be permanently removed and cannot be recovered.</li>
              <li><strong>Referrals:</strong> Any unused referral credits will be forfeited upon deletion.</li>
            </ul>
          </section>

          <section className="legal-section">
            <h2>Option 1: Delete via the Mobile App</h2>
            <p>This is the fastest way to delete your account directly from within the Fagi Errands app:</p>
            <ol style={{ paddingLeft: '20px', lineHeight: '2' }}>
              <li>Open the <strong>Fagi Errands</strong> app on your device.</li>
              <li>Log in to your account if you are not already logged in.</li>
              <li>Tap the <strong>Profile</strong> icon in the bottom navigation bar.</li>
              <li>Scroll down and tap <strong>Settings</strong>.</li>
              <li>Select <strong>Account</strong> from the settings menu.</li>
              <li>Tap <strong>Delete Account</strong> at the bottom of the page.</li>
              <li>Read the confirmation warning carefully.</li>
              <li>Enter your password to confirm your identity.</li>
              <li>Tap <strong>Confirm Delete</strong>.</li>
            </ol>
            <p>Your account will be scheduled for deletion immediately. You will receive a confirmation email.</p>
          </section>

          <section className="legal-section">
            <h2>Option 2: Request Deletion via Email</h2>
            <p>
              If you are unable to access the app, you can request account deletion by sending an email to our support
              team. Your request will be processed within <strong>7 business days</strong>.
            </p>
            <p>Send your request to:</p>
            <div className="contact-info">
              <p><strong>Email:</strong> <a href="mailto:contact.support@fagierrands.com">contact.support@fagierrands.com</a></p>
            </div>
            <p>Your email must include the following information:</p>
            <ul>
              <li>Your full name as registered on the account.</li>
              <li>The phone number or email address linked to your account.</li>
              <li>Subject line: <strong>"Account Deletion Request"</strong></li>
              <li>A brief reason for deletion (optional, but helps us improve our service).</li>
            </ul>
            <p>
              Our support team will verify your identity before processing the deletion to protect your account from
              unauthorized removal.
            </p>
          </section>

          <section className="legal-section">
            <h2>What Happens to Your Data</h2>
            <p>
              Upon account deletion, the following will occur:
            </p>
            <ul>
              <li><strong>Immediate removal:</strong> Your profile information, saved addresses, payment methods, and preferences are removed from active systems immediately.</li>
              <li><strong>Order history:</strong> Your order history may be retained in anonymized form for up to <strong>90 days</strong> for legal, financial, and audit compliance purposes, after which it is permanently deleted.</li>
              <li><strong>Financial records:</strong> Transaction records may be retained for up to <strong>7 years</strong> as required by Kenyan financial regulations.</li>
              <li><strong>Backup systems:</strong> Your data may persist in encrypted backup systems for up to <strong>30 days</strong> before being fully purged.</li>
            </ul>
          </section>

          <section className="legal-section">
            <h2>Reactivating Your Account</h2>
            <p>
              Once your account has been deleted, it cannot be reactivated. If you wish to use Fagi Errands again in the
              future, you will need to create a new account from scratch. Your previous order history and rewards will
              not be recoverable.
            </p>
          </section>

          <section className="legal-section">
            <h2>Need Help?</h2>
            <p>
              If you encounter any issues during the deletion process or have questions about your data, our support team
              is available 24/7.
            </p>
            <div className="contact-info">
              <p><strong>Email:</strong> <a href="mailto:contact.support@fagierrands.com">contact.support@fagierrands.com</a></p>
              <p><strong>Phone:</strong> <a href="tel:+254748370829">+254 748 370 829</a></p>
              <p><strong>Office:</strong> Next Gen Mall, Mombasa Road, 3rd Floor, Nairobi, Kenya</p>
            </div>
          </section>
        </div>

        <div className="legal-footer">
          <Link to="/" className="back-link">← Back to Home</Link>
          <div>
            <Link to="/privacy" className="related-link">Privacy Policy</Link>
            <Link to="/terms" className="related-link" style={{ marginLeft: '1rem' }}>Terms of Service</Link>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AccountDeletionPage;
